import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api';
import { TaskModal } from '../components/TaskModal';
import type { TaskStatusKey, TaskSummary } from '../types';

type ListResponse = { items: TaskSummary[] };

const FILTER_KEYS: ('all' | TaskStatusKey | 'high')[] = ['all', 'open', 'high', 'done'];

export function Tasks({ searchQuery }: { searchQuery: string }) {
  const { t, i18n } = useTranslation(['tasks', 'common']);
  const [tasks, setTasks] = useState<TaskSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | TaskStatusKey | 'high'>('open');
  const [modal, setModal] = useState<{ task?: TaskSummary } | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiFetch<ListResponse>('/api/tasks');
      setTasks(d.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const filtered = useMemo(() => {
    let result = tasks;
    if (filter === 'high') result = result.filter((tt) => tt.priority === 'high' && tt.status === 'open');
    else if (filter !== 'all') result = result.filter((tt) => tt.status === filter);
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      result = result.filter(
        (tt) =>
          tt.title.toLowerCase().includes(q) ||
          (tt.description ?? '').toLowerCase().includes(q),
      );
    }
    return result;
  }, [tasks, filter, searchQuery]);

  // Quick-toggle done/open
  async function toggleDone(task: TaskSummary) {
    const newStatus: TaskStatusKey = task.status === 'done' ? 'open' : 'done';
    // Optimistic
    setTasks((prev) =>
      prev.map((tt) =>
        tt.id === task.id
          ? {
              ...tt,
              status: newStatus,
              completedAt: newStatus === 'done' ? new Date().toISOString() : null,
            }
          : tt,
      ),
    );
    try {
      await apiFetch(`/api/tasks/${task.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus }),
      });
    } catch (e) {
      // Revert on failure
      setTasks((prev) => prev.map((tt) => (tt.id === task.id ? task : tt)));
      alert(t('common:errors.with_message', { message: e instanceof Error ? e.message : t('common:errors.unknown') }));
    }
  }

  if (loading) return <div className="empty">{t('list.loading')}</div>;
  if (error) return <div className="empty">{t('common:errors.with_message', { message: error })}</div>;

  // Metrics row (mockup parity — open / done today / overdue / this week)
  const now = new Date();
  const todayStr = now.toDateString();
  const oneWeekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const openCount = tasks.filter((tt) => tt.status === 'open').length;
  const highCount = tasks.filter((tt) => tt.status === 'open' && tt.priority === 'high').length;
  const doneToday = tasks.filter(
    (tt) => tt.completedAt && new Date(tt.completedAt).toDateString() === todayStr,
  ).length;
  const overdue = tasks.filter(
    (tt) => tt.status === 'open' && tt.dueAt && new Date(tt.dueAt) < now,
  ).length;
  const thisWeek = tasks.filter(
    (tt) => tt.dueAt && new Date(tt.dueAt) >= now && new Date(tt.dueAt) <= oneWeekFromNow,
  ).length;

  return (
    <div>
      <div className="metrics" style={{ marginBottom: 18 }}>
        <div className="metric">
          <div className="metric-label">{t('metrics.open')}</div>
          <div className="metric-val">{openCount}</div>
          {highCount > 0 && <div className="metric-sub down">{highCount} {t('priorities.high')}</div>}
        </div>
        <div className="metric">
          <div className="metric-label">{t('metrics.done_today')}</div>
          <div className="metric-val">{doneToday}</div>
        </div>
        <div className="metric">
          <div className="metric-label">{t('metrics.overdue')}</div>
          <div className="metric-val">{overdue}</div>
          {overdue > 0 && <div className="metric-sub down">{t('due.overdue')}</div>}
        </div>
        <div className="metric">
          <div className="metric-label">{t('metrics.this_week')}</div>
          <div className="metric-val">{thisWeek}</div>
        </div>
      </div>

      <div className="filter-row">
        {FILTER_KEYS.map((key) => (
          <div
            key={key}
            className={`type-btn ${filter === key ? 'active' : ''}`}
            onClick={() => setFilter(key)}
            style={{ flex: 'unset', padding: '6px 14px' }}
          >
            {key === 'high' ? t('filters.high_only') : t(`filters.${key}`)}
          </div>
        ))}
      </div>

      <div className="full-panel">
        <div className="panel-header">
          <span>{t('title')}</span>
          <button className="btn-link" onClick={() => setModal({})}>
            + {t('form.title_new')}
          </button>
        </div>
        {filtered.length === 0 ? (
          <div className="empty">
            {searchQuery.trim() ? t('list.empty_search') : t('list.empty')}
          </div>
        ) : (
          filtered.map((task) => (
            <div
              key={task.id}
              className="task-row"
              style={{ cursor: 'pointer' }}
              onClick={() => setModal({ task })}
            >
              <div
                className={`task-check ${task.status === 'done' ? 'done' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleDone(task);
                }}
              />
              <div className="task-info">
                <div className={`task-text ${task.status === 'done' ? 'done' : ''}`}>{task.title}</div>
                <div className="task-meta">
                  {task.dueAt && (
                    <span className={`task-date ${isUrgent(task.dueAt) ? 'urgent' : ''}`}>
                      {formatDue(task.dueAt, i18n.language)}
                    </span>
                  )}
                  <span className={`task-priority prio-${task.priority}`}>
                    {t(`priorities.${task.priority}`)}
                  </span>
                  {task.assignee && <span className="task-assign">· {task.assignee.name ?? task.assignee.email}</span>}
                  {task.case && <span className="task-assign">· {task.case.caseNumber}</span>}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {modal && (
        <TaskModal
          {...(modal.task ? { initial: modal.task } : {})}
          onSaved={() => {
            setModal(null);
            void reload();
          }}
          onClose={() => setModal(null)}
          onDeleted={() => {
            setModal(null);
            void reload();
          }}
        />
      )}
    </div>
  );
}

function isUrgent(iso: string): boolean {
  const d = new Date(iso);
  const now = new Date();
  return d < now || (d.getTime() - now.getTime()) < 2 * 24 * 60 * 60 * 1000;
}

function formatDue(iso: string, locale: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  const sameAsTomorrow = d.toDateString() === tomorrow.toDateString();
  if (sameDay) return locale.startsWith('he') ? 'היום' : 'Today';
  if (sameAsTomorrow) return locale.startsWith('he') ? 'מחר' : 'Tomorrow';
  return d.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
}

